import i18n from "../../Localization/i18next";
import { getExerciseSearchService } from "../Exercise/Search/ExerciseSearchService";
import Attribute from "../Firestore/Attribute";

/**
 * Combo-driven ExerciseSearch tests
 *
 * Builds each query from combosPayload like:
 * - query = t(base_exercise_name) + " " + t(mod1) + " " + t(mod2) ...
 *
 * Then runs ExerciseSearchService.search(query, exercises, modifiers)
 * and checks that the TOP result's words match the query's words.
 */

function normalizeForWordCompare(str) {
  if (!str) return "";
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenizeForWordCompare(str) {
  const normalized = normalizeForWordCompare(str);
  return normalized ? normalized.split(" ").filter(Boolean) : [];
}

function multiset(tokens) {
  const map = new Map();
  for (const t of tokens) {
    map.set(t, (map.get(t) || 0) + 1);
  }
  return map;
}

function multisetEqual(aTokens, bTokens) {
  if (aTokens.length !== bTokens.length) return false;
  const a = multiset(aTokens);
  const b = multiset(bTokens);
  if (a.size !== b.size) return false;
  for (const [k, v] of a.entries()) {
    if (b.get(k) !== v) return false;
  }
  return true;
}

function normalizeExercisesForSearch(exercises) {
  if (!Array.isArray(exercises)) return [];

  return exercises
    .filter(Boolean)
    .map((e) => ({
      id: e.exercise || e.id,
      name: e.name,
      originalName: e.original_name || e.originalName || e.name,
      type: e.type,
      order: e.order,
    }))
    .filter((e) => Boolean(e.id) && Boolean(e.name) && Boolean(e.originalName));
}

function normalizeModifiersForSearch(modifiers) {
  if (!Array.isArray(modifiers)) return [];

  return modifiers
    .filter(Boolean)
    .map((m) => ({
      id: m.modifier || m.id,
      name: m.name,
      title: m.title,
      subtitle: m.subtitle ?? null,
      order: m.order,
    }))
    .filter((m) => Boolean(m.id) && Boolean(m.name) && Boolean(m.title));
}

function buildFallbackTranslationsFromKeys(keys) {
  const out = {};
  for (const key of keys) {
    if (!key) continue;
    if (typeof i18n.exists === "function" && i18n.exists(key)) continue;
    out[key] = key;
  }
  return out;
}

function ensureResourceBundle(lang) {
  const hasBundle = i18n.hasResourceBundle(lang, "translation");
  if (!hasBundle) {
    i18n.addResourceBundle(lang, "translation", {}, true, true);
  }
}

async function ensureLanguage(lang) {
  ensureResourceBundle(lang);
  if (i18n.language !== lang) {
    try {
      await i18n.changeLanguage(lang);
    } catch (e) {
      i18n.language = lang;
    }
  }
}

function tSafe(key) {
  // We still call i18n.t per requirements; fallback translations
  // are injected so unknown keys resolve to themselves.
  return i18n.t(key);
}

function buildQueryFromCombo(
  combo,
  { modifiersByKey, language, addConForEquipmentInSpanish } = {}
) {
  const base = tSafe(combo.base_exercise_name);

  const modifierKeys = Array.isArray(combo.modifier_names)
    ? combo.modifier_names.filter(Boolean)
    : [];

  // Default behavior: base + all modifiers (kept stable)
  if (!(addConForEquipmentInSpanish && language === "es")) {
    const mods = modifierKeys.map((m) => tSafe(m));
    return [base, ...mods].filter((s) => String(s).trim().length > 0).join(" ");
  }

  // Test mode for Spanish:
  // If combo contains any equipment modifier(s), insert the word "con"
  // before the equipment modifiers in the generated query.
  const equipmentKeys = [];
  const otherKeys = [];

  for (const key of modifierKeys) {
    const mod = modifiersByKey?.get(key);
    if (mod?.title === Attribute.AttributeTitles.equipment) {
      equipmentKeys.push(key);
    } else {
      otherKeys.push(key);
    }
  }

  const otherLabels = otherKeys.map((k) => tSafe(k));
  const equipmentLabels = equipmentKeys.map((k) => tSafe(k));

  const parts = [base, ...otherLabels];
  if (equipmentLabels.length > 0) {
    parts.push("con", ...equipmentLabels);
  } else {
    // No equipment modifiers: keep behavior the same
    parts.push(...equipmentLabels);
  }

  return parts.filter((s) => String(s).trim().length > 0).join(" ");
}

function createDatasetLookup({ exercises, modifiers }) {
  const exerciseNameSet = new Set();
  const exerciseOriginalSet = new Set();
  for (const ex of exercises || []) {
    if (ex?.name) exerciseNameSet.add(ex.name);
    if (ex?.originalName) exerciseOriginalSet.add(ex.originalName);
  }

  const modifierNameSet = new Set();
  for (const mod of modifiers || []) {
    if (mod?.name) modifierNameSet.add(mod.name);
  }

  const hasExerciseKey = (key) =>
    Boolean(key) && (exerciseNameSet.has(key) || exerciseOriginalSet.has(key));
  const hasModifierKey = (key) => Boolean(key) && modifierNameSet.has(key);

  return {
    hasExerciseKey,
    hasModifierKey,
  };
}

function buildTopResultText(topResult) {
  if (!topResult) return "";
  const base = topResult.exerciseName || "";
  const mods = Array.isArray(topResult.modifiers)
    ? topResult.modifiers.map((m) => m.name)
    : [];
  return [base, ...mods].filter((s) => String(s).trim().length > 0).join(" ");
}

function filterOptionalSpanishCon(
  tokens,
  { language, addConForEquipmentInSpanish } = {}
) {
  if (!(addConForEquipmentInSpanish && language === "es")) return tokens;
  return (Array.isArray(tokens) ? tokens : []).filter((t) => t !== "con");
}

export async function runCombosExerciseSearchWordMatchTestsFromData({
  rawExercisesInput,
  rawModifiersInput,
  combosPayload,
  language = "en",
  limit = null,
  verbose = true,
  maxFailuresToPrint = 30,
  addConForEquipmentInSpanish = false,
  topNToMatch = 4,
} = {}) {
  const exercises = normalizeExercisesForSearch(rawExercisesInput);
  const modifiers = normalizeModifiersForSearch(rawModifiersInput);

  const combos = Array.isArray(combosPayload?.combos)
    ? combosPayload.combos
    : [];

  await ensureLanguage(language);

  // Build fallback translations so i18n.t("Comp") => "Comp" etc.
  const allKeys = [
    ...exercises.map((e) => e.name),
    ...modifiers.map((m) => m.name),
    ...modifiers.map((m) => m.subtitle).filter(Boolean),
    ...combos.map((c) => c.base_exercise_name),
    ...combos.flatMap((c) => c.modifier_names || []),
  ];
  const fallbacks = buildFallbackTranslationsFromKeys(allKeys);
  i18n.addResourceBundle(language, "translation", fallbacks, true, true);

  const svc = getExerciseSearchService();
  svc.invalidateIndex();

  const lookup = createDatasetLookup({ exercises, modifiers });
  const modifiersByKey = new Map((modifiers || []).map((m) => [m.name, m]));

  const effectiveCombos =
    typeof limit === "number" && limit >= 0 ? combos.slice(0, limit) : combos;

  const failures = [];
  const skipped = [];
  let skippedMissingAny = 0;
  let skippedMissingBase = 0;
  let skippedMissingModifier = 0;
  let passed = 0;
  let failed = 0;

  for (const combo of effectiveCombos) {
    const baseKey = combo?.base_exercise_name;
    const modifierKeys = Array.isArray(combo?.modifier_names)
      ? combo.modifier_names
      : [];

    const baseExists = lookup.hasExerciseKey(baseKey);
    const missingModifiers = modifierKeys.filter(
      (k) => !lookup.hasModifierKey(k)
    );

    // If the user deleted an exercise/modifier since the combos file was created,
    // we treat this as an expected skip (not a search logic failure).
    if (!baseExists || missingModifiers.length > 0) {
      skippedMissingAny += 1;
      if (!baseExists) skippedMissingBase += 1;
      if (missingModifiers.length > 0) skippedMissingModifier += 1;

      skipped.push({
        base_exercise_name: baseKey,
        modifier_names: modifierKeys,
        reason: !baseExists
          ? missingModifiers.length > 0
            ? "missing_base_and_modifiers"
            : "missing_base"
          : "missing_modifiers",
        missingModifiers,
      });
      continue;
    }

    const query = buildQueryFromCombo(combo, {
      modifiersByKey,
      language,
      addConForEquipmentInSpanish,
    });

    const inputTokens = filterOptionalSpanishCon(
      tokenizeForWordCompare(query),
      {
        language,
        addConForEquipmentInSpanish,
      }
    );
    const results = svc.search(query, exercises, modifiers) || [];
    const top = results[0] || null;

    if (!top) {
      failed += 1;
      failures.push({
        query,
        reason: "No results",
        inputTokens,
        topText: "",
        topTokens: [],
        topCandidates: [],
      });
      continue;
    }

    const n = Math.max(1, Number.isFinite(topNToMatch) ? topNToMatch : 4);
    const candidates = results.slice(0, n);

    const candidateTokens = candidates.map((r) => {
      const txt = buildTopResultText(r);
      return {
        text: txt,
        tokens: filterOptionalSpanishCon(tokenizeForWordCompare(txt), {
          language,
          addConForEquipmentInSpanish,
        }),
      };
    });

    const okIndex = candidateTokens.findIndex((c) =>
      multisetEqual(inputTokens, c.tokens)
    );

    if (okIndex !== -1) {
      passed += 1;
    } else {
      failed += 1;
      const topText = buildTopResultText(top);
      const topTokens = filterOptionalSpanishCon(
        tokenizeForWordCompare(topText),
        {
          language,
          addConForEquipmentInSpanish,
        }
      );

      failures.push({
        query,
        reason: `No word-match in top ${n}`,
        inputTokens,
        topText,
        topTokens,
        topCandidates: results
          .slice(0, 10)
          .map((r) => buildTopResultText(r))
          .map((s) => tokenizeForWordCompare(s).join(" ")),
      });
    }
  }

  if (verbose) {
    // eslint-disable-next-line no-console
    console.log(
      `[ExerciseSearchCombos] ${passed}/${
        passed + failed
      } passed (lang=${language}). Failures: ${failed}. Skipped(missing): ${skippedMissingAny}`
    );

    if (skippedMissingAny > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[ExerciseSearchCombos] Skipped breakdown: missing base=${skippedMissingBase}, missing modifier(s)=${skippedMissingModifier}`
      );
    }

    for (const f of failures.slice(0, Math.max(0, maxFailuresToPrint))) {
      // eslint-disable-next-line no-console
      console.log(`\nFAIL: ${f.reason}`);
      // eslint-disable-next-line no-console
      console.log(`  query: "${f.query}"`);
      // eslint-disable-next-line no-console
      console.log(`  input: ${f.inputTokens.join(" ")}`);
      // eslint-disable-next-line no-console
      console.log(`  top:   ${f.topTokens.join(" ")}`);
      // eslint-disable-next-line no-console
      console.log(`  topCandidates:`, f.topCandidates);
    }

    if (failures.length > maxFailuresToPrint) {
      // eslint-disable-next-line no-console
      console.log(
        `... plus ${failures.length - maxFailuresToPrint} more failures`
      );
    }
  }

  return {
    coachId: combosPayload?.coach_id,
    dataset: {
      exercisesCount: exercises.length,
      modifiersCount: modifiers.length,
      combosCount: combos.length,
      combosTested: effectiveCombos.length,
    },
    passed,
    failed,
    failures,
    skippedMissingAny,
    skippedMissingBase,
    skippedMissingModifier,
    skipped,
    addConForEquipmentInSpanish,
  };
}
